<template>
  <div :class="['w-auto currency-select', { 'currency-styled': styled, 'currency-select--anchored': anchoredMenu, 'currency-select--money-field': moneyField }]">
    <v-select ref="currencySelect" v-model="selectedCurrency" @option:selected="handleSelect" :options="currencies || []" label="name" :clearable="false"
      :searchable="false" :append-to-body="anchoredMenu" :calculate-position="positionCurrencyMenu"
      :class="{ 'money-currency-select': moneyField }"
      :disabled="anchoredMenu && currencies.length < 2">
      <template v-slot:[slotName]="currency" v-for="slotName in ['option', 'selected-option']">
        <div class="overflow-visible whitespace-nowrap" :class="{ 'money-currency-option': anchoredMenu && slotName === 'option' }" :key="slotName">
          <img :src="flagUrl(currency.name)" class="inline align-middle flag-style"
            style="min-width: 34px; max-width: 34px;" />
          <span v-if="!moneyField || slotName === 'option'" class="ml-2 align-middle">{{ humanizeCurrency(currency.name) }}</span>
        </div>
      </template>
      <template #no-options>
        No hay monedas disponible
      </template>
    </v-select>
  </div>
</template>

<style scoped>
.currency-select>>>.vs__dropdown-toggle {
  width: fit-content;
  height: 50px;
  background-color: #FFF;
  border-radius: 0.8rem;
}

.currency-select>>>.vs__dropdown-menu {
  min-width: 100%;
  border-radius: 0.8rem;
}

.currency-select>>>.vs__actions {
  margin-right: 10px;
}

.currency-styled>>>.vs__dropdown-toggle {
  border-radius: 27px;
  border: 2px solid #000;
  background: #F9F9F9;
  box-shadow: 3px 3px 0px 0px #611DE9;
}

.currency-select--anchored,
.currency-select--anchored>>>.vs__dropdown-toggle {
  width: 172px;
}

.currency-select--anchored>>>.vs--disabled .vs__actions {
  visibility: hidden;
}

.currency-select--money-field {
  flex: 0 0 83px;
  width: 83px;
  min-width: 83px;
}

.currency-select--money-field>>>.vs__dropdown-toggle {
  width: 83px;
  height: 77px;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.currency-select--money-field>>>.vs__actions {
  margin-right: 0;
}
</style>

<script>
import { humanizeCurrency } from '../../../utils/money_utils'
import { positionMoneyCurrencyMenu } from '../../../money-currency-menu'

const flags = import.meta.glob('../../../../assets/images/coin_flags/*.svg', { eager: true, as: 'url' });

export default {
  watch: {
    currencies: {
      handler(newCurrencies, oldCurrencies) {
        this.selectedCurrency = newCurrencies[0];
      },
      immediate: true
    }
  },
  beforeMount() {
    const urlParams = new URLSearchParams(window.location.search);

    const requested = this.initialCurrency || this.value || urlParams.get('currency');
    this.selectedCurrency = this.currencies.includes(requested) ? requested : this.currencies[0];
    this.appliedCurrency = this.selectedCurrency;
  },
  mounted() {
    this.syncMoneyFieldLabel();
  },
  updated() {
    this.syncMoneyFieldLabel();
  },
  props: {
    currencies: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
    },
    initialCurrency: {
      type: String,
      default: '',
    },
    anchoredMenu: {
      type: Boolean,
      default: false,
    },
    moneyField: {
      type: Boolean,
      default: false,
    },
    redirectBase: {
      type: String,
      default: '',
    },
    styled: {
      type: Boolean,
      default: false,
    },
    // La página aplica la moneda sin navegar (evento cancelable
    // `currency-select:change`); si nadie lo atiende, se redirige como antes.
    inPlace: {
      type: Boolean,
      default: false,
    },
		redirectOnSelect: {
			type: Boolean,
			default: true, // por defecto sí redirige (para no romper otros usos)
		}
  },
  data() {
    return {
      selectedCurrency: '',
      appliedCurrency: '',
      humanizeCurrency,
    };
  },
  methods: {
    syncMoneyFieldLabel() {
      if (!this.moneyField) return;
      // vue-select fixes this label internally. Its search input references
      // the combobox, so both controls announce the selected currency.
      const combobox = this.$refs.currencySelect?.$el.querySelector('[role="combobox"]');
      combobox?.setAttribute('aria-label', this.humanizeCurrency(this.selectedCurrency));
    },
    positionCurrencyMenu(menu, component) {
      if (!this.moneyField) menu.classList.add('money-currency-menu--right');
      return positionMoneyCurrencyMenu(menu, component);
    },
    // Avisa a la página y devuelve true si ella aplicó la moneda sin navegar.
    // revert() deja el selector en la moneda que la página sigue mostrando
    // (la anterior, salvo que se indique otra) cuando no pudo aplicarla.
    applyInPlace(currency) {
      const previous = this.appliedCurrency;
      const event = new CustomEvent('currency-select:change', {
        bubbles: true,
        cancelable: true,
        detail: {
          currency,
          previous,
          revert: (shown = previous) => {
            this.selectedCurrency = shown;
            this.appliedCurrency = shown;
          },
        },
      });
      this.appliedCurrency = currency;
      if (!this.$el.dispatchEvent(event)) return true;

      this.appliedCurrency = previous;
      return false;
    },
    handleSelect(selected) {
			// vue-select 4 emite `option:selected` (con la opción elegida), NO `input`
			// como v3 — el listener viejo quedó muerto tras la migración a Vue 3, así
			// que el registro guardaba la moneda por defecto ignorando la elección del
			// usuario (moneda del ledger). Usamos el argumento del evento (no
			// this.selectedCurrency) para no depender del orden de actualización del
			// v-model. `selected` es el string de moneda (options son strings).
			const currency = selected || this.selectedCurrency;

			if (this.value) {
				document.getElementById('transfer_source_currency_hidden').value = currency;
				return;
			}

			if (this.inPlace) {
				if (currency === this.appliedCurrency || this.applyInPlace(currency)) return;
			}

			if (this.redirectOnSelect) {
				const url = new URL(this.redirectBase || window.location.href, window.location.origin);
				url.searchParams.set('currency', currency);
				window.location = url;
			}
		},
    flagUrl(currency) {
      return flags[`../../../../assets/images/coin_flags/${currency}.svg`];
    }
  }
};
</script>
